import { getDefaultTimeZoneOnBrowser } from './dates';
import moment from 'moment';

export const EMPTY_REVIEW = 'no content';

// Minutes a customer has to confirm payment after `request-payment` creates the
// PaymentIntent, before the backend `expire-payment` transition cancels and
// refunds the transaction. This MUST match the `PT15M` period in every
// transaction process EDN (`transition/expire-payment`). Keep every client-side
// reference to this window pointed at this constant.
export const PAYMENT_CONFIRMATION_WINDOW_MINUTES = 15;
export const FLAT_RATE = 'flatRate';
export const PERCENTAGE_OF_RENTAL = 'percentageOfRental';
export const USAGE_MULTIPLE = 'usageMultiple';

export const FEE_TYPE_FIELD = 'feeType';
export const WHEN_TO_APPLY_FIELD = 'whenToApply';
export const FEE_CALC_TYPE_FIELD = 'feeCalculationType';
export const CHECKOUT_OPTIONS_FIELD = 'checkoutOptions';

export const REQUIRED_UPFRONT = 'requiredUpfront';
export const REQUIRED_UPFRONT_OPTIONAL = 'requiredUpfrontOptional';
export const DELIVERY_FEE = 'deliveryFee';
export const PICKUP_FEE = 'pickupFee';
export const SHIPPING_FEE = 'shippingFee';
export const CLEANING_FEE = 'cleaningFee';
export const SETUP_FEE = 'setupFee';
export const ENVIRONMENTAL_FEE = 'environmentalFee';
export const FUEL_FEE = 'fuelFee';
export const USAGE_FEE = 'usageFee';
export const ACCESSORY_FEE = 'accessoryFee';
export const CONSUMABLES_FEE = 'consumablesFee';
export const DEVELOPMENT_ENV = 'local';

const env = process.env.REACT_APP_PROJECT_ENV || DEVELOPMENT_ENV;

export const verificationStatuses = {
  APPROVED: 'approved',
  COMPLETED: 'completed',
  DECLINED: 'declined',
  REDACTED: 'redacted',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

export const IS_DEVELOPMENT_ENV = env === DEVELOPMENT_ENV;
export const FEE_DESCRIPTION_FIELD = 'feeDescription';

export const ONLY_CHARGE_ONCE_FOR_ENTIRE_CART = 'oncePerCart';
export const CHARGE_FOR_THIS_ITEM_INDIVIDUALLY = 'oncePerItem';

export const FEE_TYPE_OPTIONS = [
  { value: DELIVERY_FEE, label: 'Delivery Fee' },
  { value: PICKUP_FEE, label: 'Pickup Fee' },
  { value: SHIPPING_FEE, label: 'Shipping Fee' },
  { value: CLEANING_FEE, label: 'Cleaning Fee' },
  { value: SETUP_FEE, label: 'Setup Fee' },
  { value: ENVIRONMENTAL_FEE, label: 'Environmental Fee' },
  { value: FUEL_FEE, label: 'Fuel Fee' },
];

export const WHEN_TO_APPLY_OPTIONS = [
  { value: REQUIRED_UPFRONT_OPTIONAL, label: 'Optional When Booking' },
  { value: REQUIRED_UPFRONT, label: 'Required When Booking' },
];

export const FEE_CALC_TYPE_OPTIONS = [
  { value: FLAT_RATE, label: 'Flat Fee' },
  { value: PERCENTAGE_OF_RENTAL, label: 'Percentage of Rental' },
  { value: USAGE_MULTIPLE, label: 'Usage Multiple' },
];

export const CHECKOUT_OPTIONS = [
  {
    value: ONLY_CHARGE_ONCE_FOR_ENTIRE_CART,
    label: 'Only charge once for the entire cart',
  },
  {
    value: CHARGE_FOR_THIS_ITEM_INDIVIDUALLY,
    label: 'Charge for this item individually',
  },
];

export const feeTypeMapper = {
  deliveryFee: 'Delivery Fee',
  pickupFee: 'Pickup Fee',
  shippingFee: 'Shipping Fee',
  cleaningFee: 'Cleaning Fee',
  setupFee: 'Setup Fee',
  environmentalFee: 'Environmental Fee',
  fuelFee: 'Fuel Fee',
  usageFee: 'Usage Fee',
  accessoryFee: 'Accessory Fee',
  consumablesFee: 'Consumables Fee',
};

export const urgencyFieldOptions = [
  { value: 'I need a reply ASAP', label: 'I need a reply ASAP' },
  { value: 'I can wait 24 hours', label: 'I can wait 24 hours' },
  { value: "I'm flexible", label: "I'm flexible" },
];

export const durations = [
  { value: 'flexible', label: 'Flexible' },
  { value: 'specific', label: 'Specific' },
];

export const FLEXIBLE_DURATION = 'flexible';
export const SPECIFIC_DURATION = 'specific';
export const durationTypes = [
  { value: 'Hours', label: 'Hours' },
  { value: 'Days', label: 'Days' },
];

export const FLEXIBLE_DATE = 'flexible';
export const SPECIFIC_DATE = 'specific';
export const dateOptions = [
  { value: 'flexible', label: 'Flexible' },
  { value: 'specific', label: 'Specific' },
];

export const deliverPickupOptions = [
  { label: 'Yes', value: 'yes' },
  { label: 'No, you will pickup', value: 'no, you will pickup' },
  { label: 'No applicable', value: 'no applicable' },
];
export const DELIVERY_YES = 'yes';
export const DELIVERY_NO = 'no, you will pickup';
export const DELIVERY_NOT_APPLICABLE = 'no applicable';

export const radiusOptions = [
  { label: '5 km', value: '5 km' },
  { label: '10 km', value: '10 km' },
  { label: '15 km', value: '15 km' },
  { label: '20 km', value: '20 km' },
  { label: '25 km', value: '25 km' },
];

export const budgetOptions = [
  { label: 'Firm', value: 'firm' },
  { label: 'Flexible', value: 'flexible' },
  { label: 'Range', value: 'range' },
];

export const BUDGET_FIRM_OPTION = 'firm';
export const BUDGET_FLEXIBLE_OPTION = 'flexible';
export const BUDGET_RANGE_OPTION = 'range';

export const wayToConnectOptions = [
  { label: 'Phone', value: 'phone' },
  { label: 'Email', value: 'email' },
];

export const timeToConnectOptions = [
  { label: 'Select best time to connect', value: '' },
  { label: '8 AM - 12 PM', value: '8 AM - 12 PM' },
  { label: '12 PM - 4 PM', value: '12 PM - 4 PM' },
  { label: '4 PM - 8 PM', value: '4 PM - 8 PM' },
];

export const FIELD_ITEM_CONDITION = 'Condition_of_the_item';
export const FIELD_INSTRUCTIONS = 'instructions';

export const PICKUP_AT_OWNER_LOCATION = 'Pickupat_owners_location';
export const facetLabelMappings = {
  category: {
    'Party-Event': 'Party & Event',
    'Equipment-Tools': 'Equipment & Tools',
    'Sports-Recreation': 'Sports & Recreation',
    'Toys-Games-Music-Books-Art': 'Toys, Games, Music, Books & Art',
    'Electronics-Furniture': 'Electronics & Furniture',
    'photography-video': 'Photography & Video',
    'Medical-Accessories': 'Medical Accessories',
    AnimalsAccessories: 'Animal Accessories',
    'Kids-Baby': 'Kids & Baby',
    Miscellaneous: 'Miscellaneous',
    Moving: 'Moving',
    'RV-Trailers': 'RV & Trailers',
    'Fashion-Accessories': 'Fashion Accessories',
    'Recreational-Vehicles': 'Recreational Vehicles',
    'watercraft-accessories': 'Watercraft Accessories',
    'Spaces-Storage': 'Spaces & Storage',
    'People-Services': 'People & Services',
  },
  security_deposit: {
    '50': '50',
    '100': '100',
    '250': '250',
    '500': '500',
    no_deposit: 'No Deposit',
  },
  access_to_rentals: {
    Pickupat_owners_location: "Pickup at Owner's Location",
    delivery_available: 'Delivery Available',
    public_meetup_location: 'Public Meetup Location',
    shipping: 'Shipping',
    service_where_required: 'Service Where Required',
    digitally: 'Digitally',
  },
  cancelation_policy: {
    flexible: 'Flexible',
    'easy-going': 'Easy Going',
    strict: 'Strict',
  },
  reason_for_listing: {
    help_the_environment: 'Help the Environment',
    'try_before-you_buy': 'Try Before You Buy',
    side_hustle: 'Side Hustle',
    in_the_rental_business: 'In the Rental Business',
    just_because: 'Just Because',
  },
  Condition_of_the_item: {
    Used_Like_New: 'Used (Like New)',
    Used_Good: 'Used (Good)',
    New: 'New',
    Used_Fair: 'Used (Fair)',
    its_a_service: "It's a Service",
  },
  Trust_and_verification: {
    "copy_of_drivers_licience": "Copy of Driver's License",
    "not_required": "Not Required",
    "2_social_media_site_links": "2 Social Media Site Links",
    "minimum_of_3_successful_rent": "Minimum of 3 Successful Rents",
    "visual_id_verification": "Visual ID Verification",
    copy_of_drivers_licience: "Copy of Driver's License",
    not_required: 'Not Required',
    '2_social_media_site_links': '2 Social Media Site Links',
    minimum_of_3_successful_rent: 'Minimum of 3 Successful Rents',
  },
};

export const ACCESS_OPTIONS_DELIVERY_AVAILABLE = 'delivery_available';
export const ACCESS_OPTIONS_PICKUP = 'Pickupat_owners_location';
export const ACCESS_OPTIONS_SHIPPING = 'shipping';
export const ACCESS_OPTIONS_PUBLIC_MEETUP = 'public_meetup_location';
export const ACCESS_OPTIONS_SERVICE_REQUIRED = 'service_where_required';
export const ACCESS_OPTIONS_DIGITALLY = 'digitally';
export const ACCESS_OPTIONS_RETURN_PICKUP = 'return_pickup';
export const ACCESS_OPTIONS = {
  PICKUP: ACCESS_OPTIONS_PICKUP,
  DELIVERY: ACCESS_OPTIONS_DELIVERY_AVAILABLE,
  SHIP: ACCESS_OPTIONS_SHIPPING,
  PUBLIC_MEETUP: ACCESS_OPTIONS_PUBLIC_MEETUP,
  SERVICE_REQUIRED: ACCESS_OPTIONS_SERVICE_REQUIRED,
  DIGITALLY: ACCESS_OPTIONS_DIGITALLY,
  RETURN_PICKUP: ACCESS_OPTIONS_RETURN_PICKUP,
};

export const DEFAULT_CURRENCY = 'USD';

export const LOCATIONINFO_COOKIE_KEY = 'userLocationInfo';
export const CURRENCY_COOKIE_KEY = 'userCurrency';

export const userPreservedStateKeys = [
  'currentUserLocationInfo',
  'currentUserCurrency',
];

export const UNREAD_MESSAGES_ROLE_ORDER = 'orders';
export const UNREAD_MESSAGES_ROLE_SALES = 'sales';

export const HOW_TO_USE_OPTION = 'howToUse';
export const LOCATIONS_OPTION = 'locations';
export const SUGGESTED_USES_OPTION = 'suggestedUses';
export const FAQS_OPTION = 'faqs';
export const LOCAL_RATES_OPTION = 'localRates';
export const ALT_TITLES_OPTION = 'altTitles';
export const REVIEW_SUMMARY_OPTION = 'reviewSummary';
export const RETURN_CARE_INSTRUCTIONS_OPTION = 'returnCareInstructions';

export const ENHANCE_LISTING_OPTIONS = [
  {
    value: HOW_TO_USE_OPTION,
    icon: 'ri-lightbulb-flash-line',
    labelId: 'EnhanceListing.howToUse.title',
    descId: 'EnhanceListing.howToUse.desc',
    defaultSelected: true,
  },
  {
    value: LOCATIONS_OPTION,
    icon: 'ri-map-pin-line',
    labelId: 'EnhanceListing.locations.title',
    descId: 'EnhanceListing.locations.desc',
    defaultSelected: false,
  },
  {
    value: SUGGESTED_USES_OPTION,
    icon: 'ri-lightbulb-line',
    labelId: 'EnhanceListing.suggestedUses.title',
    descId: 'EnhanceListing.suggestedUses.desc',
    defaultSelected: true,
  },
  {
    value: FAQS_OPTION,
    icon: 'ri-question-answer-line',
    labelId: 'EnhanceListing.faq.title',
    descId: 'EnhanceListing.faqs.desc',
    defaultSelected: true,
  },
  {
    value: LOCAL_RATES_OPTION,
    icon: 'ri-money-dollar-circle-line',
    labelId: 'EnhanceListing.localRates.title',
    descId: 'EnhanceListing.localRates.desc',
    defaultSelected: false,
  },
  {
    value: ALT_TITLES_OPTION,
    icon: 'ri-search-line',
    labelId: 'EnhanceListing.altTitles.title',
    descId: 'EnhanceListing.altTitles.desc',
    defaultSelected: true,
  },
  {
    value: REVIEW_SUMMARY_OPTION,
    icon: 'ri-star-smile-line',
    labelId: 'EnhanceListing.reviewSummary.title',
    descId: 'EnhanceListing.reviewSummary.desc',
    defaultSelected: true,
  },
  {
    value: RETURN_CARE_INSTRUCTIONS_OPTION,
    icon: 'ri-star-smile-line',
    labelId: 'EnhanceListing.returnCareInstructions.title',
    descId: 'EnhanceListing.returnCareInstructions.desc',
    defaultSelected: true,
  },
];

export const REPLACEMENTVALUE_OPTIONS = [
  { label: '$1-$250', option: '$1-$250' },
  { label: '$251-$1000', option: '$251-$1000' },
  { label: '$1001-$2500', option: '$1001-$2500' },
  { label: '$2501-$5000', option: '$2501-$5000' },
  { label: '$5000+', option: '$5000+' },
];

export const CREATING_STEP_INPUT = 'input';
export const CREATING_STEP_PROCESSING = 'processing';
export const CREATING_STEP_PREVIEW = 'preview';

export const EDITING_STEP_DETAILS = 'details';
export const EDITING_STEP_PRICING = 'pricing';
export const EDITING_STEP_AVAILABILITY = 'availability';
export const EDITING_STEP_PHOTOS = 'photos';

export const LISTING_WIZARD_CREATING_ACTION = 'creating';
export const LISTING_WIZARD_EDITING_ACTION = 'editing';

export const PER_DAY = 'perDayPrice';
export const PER_MINUTE = 'perMinutePrice';
export const PER_HOUR = 'perHourPrice';
export const TWO_DAYS = 'twoDaysPrice';
export const THREE_DAYS = 'threeDaysPrice';
export const FOUR_DAYS = 'fourDaysPrice';
export const FIVE_DAYS = 'fiveDaysPrice';
export const WEEKLY_PRICE = 'oneWeekPrice';
export const OVERNIGHT_PRICE = 'overnightPrice';
export const TWENTY_EIGHT_DAYS = 'twentyEightDaysPrice';
export const DAY_PACKAGES = [
  PER_DAY,
  TWO_DAYS,
  THREE_DAYS,
  FOUR_DAYS,
  FIVE_DAYS,
  WEEKLY_PRICE,
  OVERNIGHT_PRICE,
  TWENTY_EIGHT_DAYS,
];

export const ENABLED_VALUE = 'enabled';

export const FIELD_ADDITIONAL_FEES = 'additionalFees';
export const FIELD_ACCESS_TO_RENTALS = 'access_to_rentals';
export const aiListingFieldsOrder = [
  'title',
  'alternative_search_titles',
  'description',
  'instructions',
  'where_the_item_can_be_used',
  'suggested_usage_of_this_item',
  'category',
  'Condition_of_the_item',
  'replacement_value',
  'return_care_instructions',
  'access_to_rentals',
  'Trust_and_verification',
  'security_deposit',
  'cancelation_policy',
  'reason_for_listing',
  'additionalFees',
  'faqs',
  'online_review_summary',
];

export const LISTING_DEFAULTS = {
  Condition_of_the_item: 'Used_Fair',
  access_to_rentals: [ACCESS_OPTIONS_PICKUP],
  Trust_and_verification: 'copy_of_drivers_licience',
  cancelation_policy: 'flexible',
  security_deposit: 'no_deposit',
  reason_for_listing: ['help_the_environment', 'side_hustle'],
  quantity: 1,
  enabledOptionalFields: [
    'alternative_search_titles',
    'instructions',
    'where_the_item_can_be_used',
    'suggested_usage_of_this_item',
    'faqs',
    'online_review_summary',
    'return_care_instructions',
  ],
};

export const DEFAULT_AVAILABILITY_PLAN = {
  type: 'availability-plan/time',
  timezone:
    typeof window !== 'undefined' ? getDefaultTimeZoneOnBrowser() : 'Etc/UTC',
  entries: [
    {
      dayOfWeek: 'sun',
      seats: 1000,
      startTime: '00:00',
      endTime: '00:00',
    },
    {
      dayOfWeek: 'mon',
      seats: 1000,
      startTime: '00:00',
      endTime: '00:00',
    },
    {
      dayOfWeek: 'tue',
      seats: 1000,
      startTime: '00:00',
      endTime: '00:00',
    },
    {
      dayOfWeek: 'wed',
      seats: 1000,
      startTime: '00:00',
      endTime: '00:00',
    },
    {
      dayOfWeek: 'thu',
      seats: 1000,
      startTime: '00:00',
      endTime: '00:00',
    },
    {
      dayOfWeek: 'fri',
      seats: 1000,
      startTime: '00:00',
      endTime: '00:00',
    },
    {
      dayOfWeek: 'sat',
      seats: 1000,
      startTime: '00:00',
      endTime: '00:00',
    },
  ],
};

export const AVAILABILITY_FIELD_24_7 = 'available247';

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'es', 'zh'];

export const SUPPORTED_LANGUAGES_MAP = {
  en: { code: 'en', label: 'English', shortLabel: 'EN', countryCode: 'US' },
  fr: { code: 'fr', label: 'Français', shortLabel: 'FR', countryCode: 'FR' },
  es: { code: 'es', label: 'Español', shortLabel: 'ES', countryCode: 'ES' },
  zh: { code: 'zh', label: '中文', shortLabel: 'ZH', countryCode: 'CN' }, // Mandarin Chinese
};
export const phoneCountryCodesMap = {
  AD: '+376', // Andorra
  AE: '+971', // United Arab Emirates
  AF: '+93', // Afghanistan
  AG: '+1268', // Antigua and Barbuda
  AI: '+1264', // Anguilla
  AL: '+355', // Albania
  AM: '+374', // Armenia
  AO: '+244', // Angola
  AQ: '+672', // Antarctica
  AR: '+54', // Argentina
  AS: '+1684', // American Samoa
  AT: '+43', // Austria
  AU: '+61', // Australia
  AW: '+297', // Aruba
  AX: '+358', // Åland Islands
  AZ: '+994', // Azerbaijan
  BA: '+387', // Bosnia and Herzegovina
  BB: '+1246', // Barbados
  BD: '+880', // Bangladesh
  BE: '+32', // Belgium
  BF: '+226', // Burkina Faso
  BG: '+359', // Bulgaria
  BH: '+973', // Bahrain
  BI: '+257', // Burundi
  BJ: '+229', // Benin
  BL: '+590', // Saint Barthélemy
  BM: '+1441', // Bermuda
  BN: '+673', // Brunei
  BO: '+591', // Bolivia
  BQ: '+599', // Caribbean Netherlands
  BR: '+55', // Brazil
  BS: '+1242', // Bahamas
  BT: '+975', // Bhutan
  BV: '+47', // Bouvet Island
  BW: '+267', // Botswana
  BY: '+375', // Belarus
  BZ: '+501', // Belize
  CA: '+1', // Canada
  CC: '+61', // Cocos Islands
  CD: '+243', // Democratic Republic of the Congo
  CF: '+236', // Central African Republic
  CG: '+242', // Republic of the Congo
  CH: '+41', // Switzerland
  CI: '+225', // Côte d'Ivoire
  CK: '+682', // Cook Islands
  CL: '+56', // Chile
  CM: '+237', // Cameroon
  CN: '+86', // China
  CO: '+57', // Colombia
  CR: '+506', // Costa Rica
  CU: '+53', // Cuba
  CV: '+238', // Cape Verde
  CW: '+599', // Curaçao
  CX: '+61', // Christmas Island
  CY: '+357', // Cyprus
  CZ: '+420', // Czech Republic
  DE: '+49', // Germany
  DJ: '+253', // Djibouti
  DK: '+45', // Denmark
  DM: '+1767', // Dominica
  DO: '+1809', // Dominican Republic
  DZ: '+213', // Algeria
  EC: '+593', // Ecuador
  EE: '+372', // Estonia
  EG: '+20', // Egypt
  EH: '+212', // Western Sahara
  ER: '+291', // Eritrea
  ES: '+34', // Spain
  ET: '+251', // Ethiopia
  FI: '+358', // Finland
  FJ: '+679', // Fiji
  FK: '+500', // Falkland Islands
  FM: '+691', // Micronesia
  FO: '+298', // Faroe Islands
  FR: '+33', // France
  GA: '+241', // Gabon
  GB: '+44', // United Kingdom
  GD: '+1473', // Grenada
  GE: '+995', // Georgia
  GF: '+594', // French Guiana
  GG: '+44', // Guernsey
  GH: '+233', // Ghana
  GI: '+350', // Gibraltar
  GL: '+299', // Greenland
  GM: '+220', // Gambia
  GN: '+224', // Guinea
  GP: '+590', // Guadeloupe
  GQ: '+240', // Equatorial Guinea
  GR: '+30', // Greece
  GS: '+500', // South Georgia and the South Sandwich Islands
  GT: '+502', // Guatemala
  GU: '+1671', // Guam
  GW: '+245', // Guinea-Bissau
  GY: '+592', // Guyana
  HK: '+852', // Hong Kong
  HM: '+672', // Heard Island and McDonald Islands
  HN: '+504', // Honduras
  HR: '+385', // Croatia
  HT: '+509', // Haiti
  HU: '+36', // Hungary
  ID: '+62', // Indonesia
  IE: '+353', // Ireland
  IL: '+972', // Israel
  IM: '+44', // Isle of Man
  IN: '+91', // India
  IO: '+246', // British Indian Ocean Territory
  IQ: '+964', // Iraq
  IR: '+98', // Iran
  IS: '+354', // Iceland
  IT: '+39', // Italy
  JE: '+44', // Jersey
  JM: '+1876', // Jamaica
  JO: '+962', // Jordan
  JP: '+81', // Japan
  KE: '+254', // Kenya
  KG: '+996', // Kyrgyzstan
  KH: '+855', // Cambodia
  KI: '+686', // Kiribati
  KM: '+269', // Comoros
  KN: '+1869', // Saint Kitts and Nevis
  KP: '+850', // North Korea
  KR: '+82', // South Korea
  KW: '+965', // Kuwait
  KY: '+1345', // Cayman Islands
  KZ: '+7', // Kazakhstan
  LA: '+856', // Laos
  LB: '+961', // Lebanon
  LC: '+1758', // Saint Lucia
  LI: '+423', // Liechtenstein
  LK: '+94', // Sri Lanka
  LR: '+231', // Liberia
  LS: '+266', // Lesotho
  LT: '+370', // Lithuania
  LU: '+352', // Luxembourg
  LV: '+371', // Latvia
  LY: '+218', // Libya
  MA: '+212', // Morocco
  MC: '+377', // Monaco
  MD: '+373', // Moldova
  ME: '+382', // Montenegro
  MF: '+590', // Saint Martin
  MG: '+261', // Madagascar
  MH: '+692', // Marshall Islands
  MK: '+389', // North Macedonia
  ML: '+223', // Mali
  MM: '+95', // Myanmar
  MN: '+976', // Mongolia
  MO: '+853', // Macau
  MP: '+1670', // Northern Mariana Islands
  MQ: '+596', // Martinique
  MR: '+222', // Mauritania
  MS: '+1664', // Montserrat
  MT: '+356', // Malta
  MU: '+230', // Mauritius
  MV: '+960', // Maldives
  MW: '+265', // Malawi
  MX: '+52', // Mexico
  MY: '+60', // Malaysia
  MZ: '+258', // Mozambique
  NA: '+264', // Namibia
  NC: '+687', // New Caledonia
  NE: '+227', // Niger
  NF: '+672', // Norfolk Island
  NG: '+234', // Nigeria
  NI: '+505', // Nicaragua
  NL: '+31', // Netherlands
  NO: '+47', // Norway
  NP: '+977', // Nepal
  NR: '+674', // Nauru
  NU: '+683', // Niue
  NZ: '+64', // New Zealand
  OM: '+968', // Oman
  PA: '+507', // Panama
  PE: '+51', // Peru
  PF: '+689', // French Polynesia
  PG: '+675', // Papua New Guinea
  PH: '+63', // Philippines
  PK: '+92', // Pakistan
  PL: '+48', // Poland
  PM: '+508', // Saint Pierre and Miquelon
  PN: '+64', // Pitcairn Islands
  PR: '+1787', // Puerto Rico
  PS: '+970', // Palestine
  PT: '+351', // Portugal
  PW: '+680', // Palau
  PY: '+595', // Paraguay
  QA: '+974', // Qatar
  RE: '+262', // Réunion
  RO: '+40', // Romania
  RS: '+381', // Serbia
  RU: '+7', // Russia
  RW: '+250', // Rwanda
  SA: '+966', // Saudi Arabia
  SB: '+677', // Solomon Islands
  SC: '+248', // Seychelles
  SD: '+249', // Sudan
  SE: '+46', // Sweden
  SG: '+65', // Singapore
  SH: '+290', // Saint Helena
  SI: '+386', // Slovenia
  SJ: '+47', // Svalbard and Jan Mayen
  SK: '+421', // Slovakia
  SL: '+232', // Sierra Leone
  SM: '+378', // San Marino
  SN: '+221', // Senegal
  SO: '+252', // Somalia
  SR: '+597', // Suriname
  SS: '+211', // South Sudan
  ST: '+239', // São Tomé and Príncipe
  SV: '+503', // El Salvador
  SX: '+1721', // Sint Maarten
  SY: '+963', // Syria
  SZ: '+268', // Eswatini
  TC: '+1649', // Turks and Caicos Islands
  TD: '+235', // Chad
  TF: '+262', // French Southern Territories
  TG: '+228', // Togo
  TH: '+66', // Thailand
  TJ: '+992', // Tajikistan
  TK: '+690', // Tokelau
  TL: '+670', // East Timor
  TM: '+993', // Turkmenistan
  TN: '+216', // Tunisia
  TO: '+676', // Tonga
  TR: '+90', // Turkey
  TT: '+1868', // Trinidad and Tobago
  TV: '+688', // Tuvalu
  TW: '+886', // Taiwan
  TZ: '+255', // Tanzania
  UA: '+380', // Ukraine
  UG: '+256', // Uganda
  UM: '+1', // United States Minor Outlying Islands
  US: '+1', // United States
  UY: '+598', // Uruguay
  UZ: '+998', // Uzbekistan
  VA: '+379', // Vatican City
  VC: '+1784', // Saint Vincent and the Grenadines
  VE: '+58', // Venezuela
  VG: '+1284', // British Virgin Islands
  VI: '+1340', // United States Virgin Islands
  VN: '+84', // Vietnam
  VU: '+678', // Vanuatu
  WF: '+681', // Wallis and Futuna
  WS: '+685', // Samoa
  YE: '+967', // Yemen
  YT: '+262', // Mayotte
  ZA: '+27', // South Africa
  ZM: '+260', // Zambia
  ZW: '+263', // Zimbabwe
};
export const LISTING_ALOGLIA_REMOVABLE_FIELDS = [
  'order',
  'instructions',
  'faqs',
  'additionalFees',
  'where_the_item_can_be_used',
  'suggested_usage_of_this_item',
  'online_review_summary',
  'generatedImages',
  'faqsEnabled',
  'alternative_search_titlesEnabled',
  'instructionsEnabled',
  'suggested_usage_of_this_itemEnabled',
  'where_the_item_can_be_usedEnabled',
  'enabledOptionalFields',
  'environmental_impact',
  'listingToken',
  'replacement_value',
  'return_care_instructions',
  ...SUPPORTED_LANGUAGES,
];

/**
 * @deprecated Use filterListingUserPublicDataForAlgolia from algoliaHelpers instead.
 * This constant is kept for backward compatibility but should not be used in new code.
 * The whitelist approach in algoliaConstants.js is now the preferred method.
 */
export const LISTING_USER_PUBLIC_DATA_REMOVABLE_FIELDS = [
  'cart',
  'acceptedTransactions',
  'seoTitle',
  'seoDescription',
];

export const RICH_TEXT_FIELDS = [
  'description',
  'instructions',
  'where_the_item_can_be_used',
];

const templatesByEnv = {
  development: {
    TRANSACTION_MESSAGE: 'cea2b242-2ddc-430b-a24a-a5934ee32873', // {{message.custom_data.senderName}} sent you a new message about {{message.custom_data.itemName}}.
    TRANSACTION_INQUIRY_PROVIDER: '3db470a5-b7b8-44bd-a9f8-d1216d11e370', // {{message.custom_data.userName}} has sent you a new inquiry about {{message.custom_data.itemName}}.
    REVIEW_RECEIVED: '9410042e-c142-4a75-bfd3-0ff10d381900', // {{message.custom_data.userName}} left you a review.
    REVIEW_PUBLISHED_ON_PROFILE: 'edb2fcc6-735d-4e48-ac71-125e6d492e6b', // {{message.custom_data.userName}}'s review has been published on your profile.
    BOOKING_REQUEST_PROVIDER: '192b17aa-d052-4f60-a5c2-18ac90607615', // {{message.custom_data.userName}} requested to book {{message.custom_data.itemName}}.
    BOOKING_EXPIRED_CUSTOMER: '39f2b018-8e1e-4855-a3e9-f4d4db20b590',
    BOOKING_DECLINED_CUSTOMER: '6da59c2b-3841-45b0-abed-a040ed860422', // Your booking request for {{message.custom_data.itemName}} was declined.
    BOOKING_ACCEPTED_CUSTOMER: 'd76031aa-dcf7-4358-9407-d757f07bbeff', // Your booking request for {{message.custom_data.itemName}} was accepted.
  },
  staging: {
    TRANSACTION_MESSAGE: '34128b80-0869-4fdd-96ac-33433106b79b',
    TRANSACTION_INQUIRY_PROVIDER: 'fe4c8910-cb0c-4510-98bf-57591af9b215',
    REVIEW_RECEIVED: '22888392-d99d-4c42-b2a1-f9fa0cde3ec5',
    REVIEW_PUBLISHED_ON_PROFILE: 'd0c3bd0b-e83b-48c2-ab8b-85d025df7c30',
    BOOKING_REQUEST_PROVIDER: 'b1ad38c4-d398-41c8-a2c1-e213668e4d27',
    BOOKING_EXPIRED_CUSTOMER: '4467d1d3-6070-474e-9cb9-187afa9f7e87',
    BOOKING_DECLINED_CUSTOMER: '36caca3f-b93a-4e83-bd8e-49bad7ac061b',
    BOOKING_ACCEPTED_CUSTOMER: '0d7f342d-d893-43cd-921f-4d3219cd2094',
  },
  production: {
    TRANSACTION_MESSAGE: 'e8dfd8e4-5d69-4487-9acc-0640866bd344',
    TRANSACTION_INQUIRY_PROVIDER: '98ceaa85-5e95-4bc5-82d9-4c56f8d93e8c',
    REVIEW_RECEIVED: '8e26acfc-bd69-4562-aa70-ff7c964e9b4d',
    REVIEW_PUBLISHED_ON_PROFILE: 'a56d60a6-663c-451e-8c39-6c86495a1d5d',
    BOOKING_REQUEST_PROVIDER: '3f2643e9-c7f4-43e3-9d6b-db03cd82cdd8',
    BOOKING_EXPIRED_CUSTOMER: 'c58bb285-1265-48bd-b1a9-628287072977',
    BOOKING_DECLINED_CUSTOMER: '8fe7dd1d-b26b-4f80-905a-bd3c67347311',
    BOOKING_ACCEPTED_CUSTOMER: '51f553c0-72fe-4ba9-ad5b-c7309e38b4ad',
  },
};

export const oneSignalNotificationTemplates = templatesByEnv[env];

// TRANSACTION_INQUIRY_PROVIDER
// transition/inquire

// BOOKING_REQUEST_PROVIDER
// transition/request-payment
// transition/request-booking

// BOOKING_ACCEPTED_CUSTOMER
// transition/accept
// transition/accept-after-payment

// BOOKING_DECLINED_CUSTOMER
// transition/decline
// transition/decline-after-payment

// REVIEW_RECEIVED
// transition/review-1-by-provider
// transition/review-1-by-customer

// REVIEW_PUBLISHED_ON_PROFILE
// transition/review-2-by-customer
// transition/review-2-by-provider

export const makeFreeTransactionData = (response, currentUser) => {
  const { data, included } = response.data;

  const {
    processName,
    payoutTotal,
    processVersion,
    protectedData,
    lastTransition,
    payinTotal,
  } = data.attributes;

  const { deliveryMethod, stripePaymentIntentId } = protectedData;

  const provider = (included || []).find(st => st != null && st.type == 'user');
  const listing = (included || []).find(st => st != null && st.type == 'listing');

  const { firstName, lastName } = currentUser?.attributes?.profile || {};

  return {
    tx_id: data.id.uuid,
    tx_status: lastTransition,
    tx_process_name: processName,
    tx_process_version: processVersion,
    tx_amount: payinTotal.amount,
    tx_currency: payinTotal.currency,
    tx_delivery_type: deliveryMethod,
    tx_provider_amount: payoutTotal.amount,
    provider_id: provider.id.uuid,
    provider_name: provider?.attributes?.profile?.displayName || '',
    customer_id: currentUser.id.uuid,
    customer_name: `${firstName} ${lastName}`,
    listing_id: listing.id.uuid,
    listing_title: listing.attributes.title,
    payment_intent_id: stripePaymentIntentId,
    tx_settled_outside: 'system',
    provider_transfer_status: 'system',
  };
};

export const isCurrentUserAdmin = currentUser => {
  // Admin = profile.metadata.isAdmin set to true in Console. Members can write their
  // own privateData, so it must never decide admin. Keep in step with the server check
  // in server/api/Admin/adminMiddleWare.js, which is what actually protects /admin.
  return currentUser?.attributes?.profile?.metadata?.isAdmin === true;
};

// ID verification (Persona) lives in profile.metadata, which only the server can write (the
// Persona webhook, through the Integration API) and which every member can read, so badges on
// other people's profiles keep working. Members can write their own publicData, so the old
// publicData copies must never decide "verified". Keep in step with the server check in
// server/ constants.js.
export const isUserIdVerified = user => {
  return user?.attributes?.profile?.metadata?.isUserVerified === true;
};

export const getPersonaVerification = user => {
  const metadata = user?.attributes?.profile?.metadata || {};
  return {
    status: metadata.personaVerificationStatus || null,
    details: metadata.personaVerificationDetails || {},
  };
};

export const verificationNeededForReason = {
  1: 'OrderValue is greater than rentalValue',
  2: 'Total value of assets is greater than maxAssetValue',
  3: 'Start time is less than minBookingNoticeHours or/and total value of assets is greater than minAssetValue',
  4: 'Listing has enabled verification requirements',
};
export const isTransactionVerificationNeeded = (
  transaction = null,
  AdminConfig = {}
) => {
  const verificationNeededFor = [];
  const {
    isVerificationRequired = true,
    maxAssetValue = 200000,
    rentalValue = 100000,
    minBookingNoticeHours = 72,
  } = AdminConfig || {};
  if (!transaction) {
    return {
      verificationNeeded: false,
      verificationNeededForReason: [],
      alreadyVerified: false,
    };
  }
  const { listing = {}, booking = {}, attributes = {}, provider = {}, customer = {} } =
    transaction || {};

  // Customer is already verified, so no verification needed.
  const isCustomerVerified = isUserIdVerified(customer);

  const transactionAt = moment(attributes?.createdAt).unix();
  const customerPersonaVerifiedAt = getPersonaVerification(customer).details.verifiedAt || 0;
  const isCustomerAlreadyVerifiedBefore = customerPersonaVerifiedAt && customerPersonaVerifiedAt < transactionAt ? true : false;

  const {
    customVerificationThresholdsValues = {},
    customVerificationThresholds = false,
  } = provider?.attributes?.profile?.publicData || {};
  const { minimumOrderValue, minimumAssetValue, bookingUrgency } =
    customVerificationThresholdsValues || {};

  const currentOrderValue = attributes?.payinTotal?.amount || 0;
  const hoursFromNow = moment(booking?.attributes?.start).diff(
    moment(),
    'hours'
  );
  const { min = 0, max = 0 } =
    listing?.attributes?.publicData?.replacement_value || {};

  // Need to update the condition as per the listing data in the public data.
  const isListingVerificationEnabled = listing?.attributes?.publicData?.Trust_and_verification === 'visual_id_verification' || false;
  // In listing we need find if the user has enabled the Verification Requirements.
  // A edge case ? what if the user has not enabled the verification requirement, 
  // but the admin custom theresholds are meet for the transaction, 
  // then do we show the user to verify?
  // Is preference order is 
  // Listing Preference >  Owner Preference > Admin Preference  

  if (isListingVerificationEnabled) {
    verificationNeededFor.push(4);
  }
  // Need to give priority to the provider preferences.
  if (customVerificationThresholds) {
    if (currentOrderValue >= minimumOrderValue.amount)
      verificationNeededFor.push(1);
    if (min && minimumAssetValue.amount <= min.amount)
      verificationNeededFor.push(2);
    if (hoursFromNow <= bookingUrgency) verificationNeededFor.push(3);
  } else {
    if (currentOrderValue >= rentalValue) verificationNeededFor.push(1);
    if (min && maxAssetValue <= min.amount) verificationNeededFor.push(2);
    if (
      minBookingNoticeHours > 0 &&
      hoursFromNow <= minBookingNoticeHours
    )
      verificationNeededFor.push(3);
  }


  if (!isVerificationRequired)
    return {
      verificationNeeded: false,
      verificationNeededForReason: verificationNeededFor,
      alreadyVerified: isCustomerVerified,
    };

  return {
    verificationNeeded: isCustomerAlreadyVerifiedBefore ? false : verificationNeededFor.length > 0,
    verificationNeededForReason: verificationNeededFor,
    alreadyVerified: isCustomerVerified,
  };
};
export const isCurrentUserVerified = currentUser => isUserIdVerified(currentUser);
export const getTransactionCompleted = transaction => {
  const { attributes } = transaction || {};
  const { transitions } = attributes || {};
  const transitionNames = transitions.map(transition => transition.transition);
  const completedTransitions = [
    'transition/complete',
    'transition/operator-complete',
    'transition/complete-after-payment',
    'transition/operator-complete-after-payment',
  ];
  return transitionNames.some(name => completedTransitions.includes(name));
};
export const getTransactionAcceptedWithoutVerification = transaction => {
  const { attributes } = transaction || {};
  const { transitions } = attributes || {};
  const transitionNames = transitions.map(transition => transition.transition);
  const acceptedTransitions = [
    'transition/accept-after-payment-without-verification',
    'transition/accept-without-verification',
    'transition/operator-accept',
    'transition/operator-accept-after-payment',
  ];
  return transitionNames.some(name => acceptedTransitions.includes(name));
};
export const getThresholdValues = (currentUser = {}, adminConfig = {}) => {
  const { customVerificationThresholdsValues = {}, customVerificationThresholds = false } = currentUser?.attributes?.profile?.publicData || {};
  const { minimumOrderValue = {}, minimumAssetValue = {}, bookingUrgency = 0 } = customVerificationThresholdsValues || {};
  return {
    minimumOrderValue: customVerificationThresholds ? minimumOrderValue?.amount || 0 : adminConfig?.rentalValue || 0,
    minimumAssetValue: customVerificationThresholds ? minimumAssetValue?.amount || 0 : adminConfig?.maxAssetValue || 0,
    bookingUrgency: customVerificationThresholds ? bookingUrgency || 0 : adminConfig?.minBookingNoticeHours || 0,
  }
}